import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SERIF, UI, MONO } from '../../design/theme';

const TONE = {
  green: { bg: '#E8F5E9', fg: '#1B5E20' },
  pink: { bg: '#FCE4EC', fg: '#880E4F' },
  amber: { bg: '#FFF3E0', fg: '#5D2F00' },
  blue: { bg: '#E3F2FD', fg: '#0D3D6E' },
};

const COMMUNITY_TEXTURE = 'https://static.prod-images.emergentagent.com/jobs/66ed9a22-554e-4d84-98f7-29812d909fcd/images/d8bbd78d87e75f7691a634381dd20745c4ba78dba6fab5dd037e19e57d801821.png';

function whenLabel(when) {
  return when === 'เมื่อสักครู่' ? when : `${when}ที่แล้ว`;
}

export default function CommunityActivityBoard({ theme, activity }) {
  const navigate = useNavigate();

  return (
    <div data-testid="v2-home-activity-section" style={{ marginTop: 26 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '0 22px 12px', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 800, letterSpacing: -0.2 }}>
              วันนี้ในชุมชน
            </div>
            {activity && activity.length > 0 && <span
              data-testid="v2-home-activity-live-label"
              style={{
                fontFamily: UI, fontSize: 9, fontWeight: 900,
                padding: '4px 8px', borderRadius: 999,
                background: `${theme.safe}22`, color: theme.safe,
                letterSpacing: 0.4, textTransform: 'uppercase',
              }}
            >สดจริง</span>}
          </div>
          <div style={{ fontFamily: UI, fontSize: 12, color: theme.muted, marginTop: 3 }}>
            {activity === null ? 'กำลังดึงเรื่องใหม่...' : activity.length ? `${activity.length} เรื่องที่น่าตามต่อ` : 'ยังรอเรื่องแรกของวันนี้'}
          </div>
        </div>
        <button
          data-testid="v2-home-activity-open-community"
          onClick={() => navigate('/v2/community')}
          style={{
            border: 'none', background: `${theme.primary}12`, color: theme.primary,
            fontFamily: UI, fontSize: 12, fontWeight: 800, cursor: 'pointer',
            padding: '9px 12px', borderRadius: 999, flexShrink: 0,
          }}
        >ไปเล่น →</button>
      </div>

      <div style={{ padding: '0 16px' }}>
        <div style={{
          position: 'relative', overflow: 'hidden', borderRadius: 28,
          padding: 12, border: `1px solid ${theme.cardBorder}`,
          background: `linear-gradient(180deg, rgba(255,255,255,0.88), rgba(255,255,255,0.96)), url(${COMMUNITY_TEXTURE}) center/360px`,
          boxShadow: '0 12px 34px rgba(20, 26, 38, 0.06)',
        }}>
          {activity === null && (
            <div data-testid="v2-home-activity-loading-board" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              {[0, 1, 2].map(i => <div key={i} data-testid={`v2-home-activity-skeleton-${i}`} style={{ minHeight: i === 0 ? 150 : 104, borderRadius: 22, background: theme.skeleton, opacity: 0.72 }}/>) }
            </div>
          )}

          {activity && activity.length === 0 && <EmptyActivity theme={theme} />}
          {activity && activity.length > 0 && <ActivityBento theme={theme} activity={activity} />}
        </div>
      </div>
    </div>
  );
}

function EmptyActivity({ theme }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid="v2-home-activity-empty"
      style={{
        minHeight: 138, background: theme.surface, borderRadius: 24, padding: 18,
        border: `1px dashed ${theme.cardBorder}`, display: 'flex', alignItems: 'center', gap: 14,
      }}
    >
      <div style={{ width: 54, height: 54, borderRadius: 18, background: '#FFD93D33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 27, flexShrink: 0 }}>🐾</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: SERIF, fontSize: 19, color: theme.text, lineHeight: 1.2, fontWeight: 800 }}>
          ยังไม่มีอัปเดตจริงวันนี้
        </div>
        <div style={{ fontFamily: UI, fontSize: 12, color: theme.muted, marginTop: 5, lineHeight: 1.45 }}>
          เปิดชุมชนแล้วเลือกน้องที่อยากติดตาม หรือชวนน้องใกล้บ้านเข้ามาเป็นเรื่องแรก
        </div>
      </div>
      <button
        data-testid="v2-home-activity-empty-community"
        onClick={() => navigate('/v2/community')}
        style={{
          height: 38, padding: '0 14px', borderRadius: 999, border: 'none',
          background: theme.primary, color: '#fff', fontFamily: UI,
          fontSize: 12, fontWeight: 800, cursor: 'pointer', flexShrink: 0,
          boxShadow: `0 8px 18px ${theme.primary}33`,
        }}
      >เริ่มเล่น</button>
    </div>
  );
}

function ActivityBento({ theme, activity }) {
  const navigate = useNavigate();
  const [featured, ...rest] = activity;
  const ft = TONE[featured.tone] || TONE.green;

  return (
    <div data-testid="v2-home-activity-bento" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))', gap: 10 }}>
      <FeaturedActivity theme={theme} item={featured} onOpen={() => navigate(featured.route)} />
      <QuestCard theme={theme} />
      {rest.slice(0, 4).map((a) => <SmallActivity key={a.id} theme={theme} item={a} fallbackTone={ft} onOpen={() => navigate(a.route)} />)}
    </div>
  );
}

function FeaturedActivity({ theme, item, onOpen }) {
  return (
    <button
      data-testid={`v2-home-activity-featured-${item.id}`}
      onClick={onOpen}
      style={{
        gridColumn: '1 / -1', minHeight: 166, border: 'none', borderRadius: 24,
        padding: 0, overflow: 'hidden', cursor: 'pointer', textAlign: 'left',
        position: 'relative', color: '#fff', background: item.photo
          ? `linear-gradient(90deg, rgba(24,28,35,0.76), rgba(24,28,35,0.24)), url(${item.photo}) center/cover`
          : `linear-gradient(135deg, ${theme.primary}, ${theme.sunsetB})`,
        boxShadow: '0 10px 26px rgba(20, 26, 38, 0.14)',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.22))' }}/>
      <div style={{ position: 'relative', zIndex: 1, padding: 18, minHeight: 130, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.28)',
            borderRadius: 999, padding: '7px 10px', fontFamily: UI, fontSize: 11, fontWeight: 900,
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          }}>
            <span>{item.emoji}</span>{item.label}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 10, opacity: 0.88 }}>{whenLabel(item.when)}</span>
        </div>
        <div>
          <div style={{ fontFamily: UI, fontSize: 12, opacity: 0.9, marginBottom: 4 }}>{item.name}</div>
          <div style={{ fontFamily: SERIF, fontSize: 25, lineHeight: 1.05, fontWeight: 900, maxWidth: 430 }}>
            {item.action}
          </div>
          <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: UI, fontSize: 12, fontWeight: 900 }}>
            {item.cta} <span>→</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function QuestCard({ theme }) {
  const navigate = useNavigate();
  return (
    <div data-testid="v2-home-activity-quest-card" style={{
      minHeight: 132, borderRadius: 22, padding: 15, background: '#FFFDF9',
      border: '1px solid rgba(26,28,35,0.08)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    }}>
      <div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: theme.primary, fontWeight: 900, letterSpacing: 0.4 }}>MISSION</div>
        <div style={{ fontFamily: SERIF, fontSize: 18, lineHeight: 1.12, fontWeight: 900, marginTop: 4 }}>เลือกน้องที่อยากตามต่อ</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button data-testid="v2-home-activity-quest-swipe" onClick={() => navigate('/v2/community')} style={{ flex: 1, height: 34, border: 'none', borderRadius: 999, background: theme.primary, color: '#fff', fontFamily: UI, fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>เลือกน้อง</button>
        <button data-testid="v2-home-activity-quest-map" onClick={() => navigate('/v2/map')} style={{ width: 38, height: 34, border: `1px solid ${theme.cardBorder}`, borderRadius: 999, background: theme.surface, color: theme.text, cursor: 'pointer' }}>🗺️</button>
      </div>
    </div>
  );
}

function SmallActivity({ theme, item, fallbackTone, onOpen }) {
  const t = TONE[item.tone] || fallbackTone;
  return (
    <button
      data-testid={`v2-home-activity-${item.id}`}
      onClick={onOpen}
      style={{
        minHeight: 132, border: 'none', borderRadius: 22, padding: item.photo ? 0 : 15,
        background: item.photo ? `linear-gradient(180deg, rgba(20,26,38,.08), rgba(20,26,38,.62)), url(${item.photo}) center/cover` : theme.surface,
        color: item.photo ? '#fff' : theme.text, cursor: 'pointer', textAlign: 'left', overflow: 'hidden',
        borderTop: `5px solid ${t.fg}`, boxShadow: '0 6px 18px rgba(20,26,38,0.05)',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      }}
    >
      <div style={{ padding: item.photo ? 13 : 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ width: 36, height: 36, borderRadius: 14, flexShrink: 0, background: item.photo ? 'rgba(255,255,255,.22)' : t.bg, color: item.photo ? '#fff' : t.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, backdropFilter: item.photo ? 'blur(8px)' : 'none' }}>{item.emoji}</span>
        <span style={{ fontFamily: MONO, fontSize: 9, color: item.photo ? 'rgba(255,255,255,.86)' : theme.muted }}>{whenLabel(item.when)}</span>
      </div>
      <div style={{ padding: item.photo ? 13 : 0 }}>
        <div style={{ fontFamily: UI, fontSize: 11, color: item.photo ? 'rgba(255,255,255,.86)' : theme.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
        <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 900, lineHeight: 1.13, marginTop: 3, textShadow: item.photo ? '0 1px 10px rgba(0,0,0,.35)' : 'none' }}>{item.action}</div>
      </div>
    </button>
  );
}